import React from 'react';
import ReactDOM from 'react-dom';
import {
    color_shades_darkest,
    color_text_dashboard_secondary,
    color_shades_light,
    color_text_link,
    voicify_spark_darker
} from '../../../../../shared/constants/colors';
import { tooltipStyles } from '../../../../../shared/constants/chartTheme';

export interface AppBreakdown {
    appName: string;
    count: number;
    english?: number;
    spanish?: number;
}

interface HistogramTooltipData {
    allCalls?: number;
    containableCalls?: number;
    containedCalls?: number;
    date?: string;
}

interface ChartTooltipProps {
    title: string;
    value?: string | number;
    subtitle?: string;
    apps?: AppBreakdown[];
    showLanguageBreakdown?: boolean;
    /** When true, only show Spanish counts in language breakdown (hide English) */
    spanishOnly?: boolean;
    showApps?: boolean;
    valueLabel?: string;
    maxWidth?: string;
    maxHeight?: string;
    countPrefix?: string;
    clickHint?: boolean;
    histogramData?: HistogramTooltipData;
    /** Color for the indicator square shown next to the value */
    color?: string;
    /** Border color for the indicator square */
    borderColor?: string;
    /** Compact mode for mobile - shows simplified tooltip styling */
    compact?: boolean;
    /** Overlay mode - uses absolute positioning to overlay parent tile content (for metric tiles only) */
    overlay?: boolean;
}

export const ChartTooltip: React.FC<ChartTooltipProps> = ({
    title,
    value,
    subtitle,
    apps = [],
    showLanguageBreakdown = false,
    spanishOnly = false,
    showApps = true,
    valueLabel = 'calls',
    maxWidth = '300px',
    maxHeight = '400px',
    countPrefix = '',
    clickHint = false,
    histogramData,
    color,
    borderColor,
    compact = false,
    overlay = false
}) => {
    // Initialize to null to prevent tooltip from appearing at (0, 0) before mouse position is known
    const [position, setPosition] = React.useState<{ x: number; y: number } | null>(null);

    // Scale tooltip font sizes on small laptop screens to match compressed UI
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const isChromebook = screenWidth >= 1024 && screenWidth < 1280;
    const isSmallLaptop = screenWidth >= 1280 && screenWidth < 1440;
    const ttTitle = isChromebook ? '11px' : isSmallLaptop ? '12px' : tooltipStyles.title.fontSize;
    const ttValue = isChromebook ? '14px' : isSmallLaptop ? '15px' : tooltipStyles.value.fontSize;
    const ttSubtitle = isChromebook ? '10px' : isSmallLaptop ? '11px' : tooltipStyles.subtitle.fontSize;
    const ttBody = isChromebook ? 10 : isSmallLaptop ? 11 : 13;
    const ttSmall = isChromebook ? 9 : isSmallLaptop ? 10 : 11;

    React.useEffect(() => {
        // Skip mouse tracking for compact mode
        if (compact) return;

        const handleMouseMove = (e: MouseEvent) => {
            // Calculate position with viewport boundary checks to keep tooltip visible
            const tooltipWidth = 300; // max-width of tooltip
            const tooltipHeight = 200; // approximate height
            const padding = 15; // offset from cursor

            let x = e.clientX + padding;
            let y = e.clientY - 10;

            // Prevent tooltip from going off the right edge
            if (x + tooltipWidth > window.innerWidth) {
                x = e.clientX - tooltipWidth - padding;
            }

            // Prevent tooltip from going off the bottom edge
            if (y + tooltipHeight > window.innerHeight) {
                y = window.innerHeight - tooltipHeight - 10;
            }

            // Prevent tooltip from going off the top edge
            if (y < 10) {
                y = 10;
            }

            // Prevent tooltip from going off the left edge
            if (x < 10) {
                x = 10;
            }

            setPosition({ x, y });
        };

        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, [compact]);

    // Compact mode - render simplified tooltip (with optional absolute positioning for tile overlays)
    if (compact) {
        const overlayStyle = overlay ? {
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10
        } : {};

        return (
            <div
                style={{
                    ...overlayStyle,
                    background: tooltipStyles.container.background,
                    backdropFilter: tooltipStyles.container.backdropFilter,
                    padding: '10px 14px',
                    borderRadius: tooltipStyles.container.borderRadius,
                    boxShadow: tooltipStyles.container.boxShadow,
                    border: tooltipStyles.container.border,
                    minWidth: '200px',
                    maxWidth: '200px',
                    textAlign: 'center'
                }}
            >
                {title && (
                    <div style={{
                        ...tooltipStyles.title,
                        fontSize: '12px',
                        marginBottom: value !== undefined ? 4 : 0
                    }}>
                        {title}
                    </div>
                )}
                {value !== undefined && (
                    <div style={{ marginBottom: subtitle ? 4 : 0 }}>
                        <span style={{ ...tooltipStyles.value, fontSize: '14px' }}>
                            {typeof value === 'number' ? value.toLocaleString() : value}
                        </span>
                        {valueLabel && (
                            <span style={{ ...tooltipStyles.subtitle, marginLeft: 4, fontSize: '11px' }}>
                                {valueLabel}
                            </span>
                        )}
                    </div>
                )}
                {subtitle && (
                    <div style={{
                        ...tooltipStyles.subtitle,
                        fontSize: '10px',
                        lineHeight: 1.3
                    }}>
                        {subtitle}
                    </div>
                )}
                {clickHint && (
                    <div style={{
                        borderTop: `1px solid ${color_shades_light}`,
                        paddingTop: 6,
                        marginTop: 6,
                        fontSize: 10,
                        color: color_text_link,
                        fontStyle: 'italic',
                        textAlign: 'center'
                    }}>
                        This view is limited on Mobile. Open on Desktop for full details.
                    </div>
                )}
            </div>
        );
    }

    // Don't render tooltip until we have a valid mouse position
    if (!position) {
        return null;
    }

    // Format date for histogram tooltip
    const formatHistogramDate = (dateString?: string): string => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const tooltipContent = (
        <div
            style={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                background: tooltipStyles.container.background,
                backdropFilter: tooltipStyles.container.backdropFilter,
                padding: '12px 16px 16px',
                lineHeight: 1.5,
                borderRadius: tooltipStyles.container.borderRadius,
                boxShadow: tooltipStyles.container.boxShadow,
                border: tooltipStyles.container.border,
                minWidth: tooltipStyles.container.minWidth,
                maxWidth,
                maxHeight,
                overflow: 'auto',
                zIndex: 99999,
                pointerEvents: 'none' as const
            }}
        >
            <div style={{ ...tooltipStyles.title, marginBottom: value !== undefined || subtitle || histogramData || (showApps && apps.length > 0) || clickHint ? tooltipStyles.title.marginBottom : 0, fontSize: ttTitle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{histogramData?.date ? formatHistogramDate(histogramData.date) : title}</span>
                {spanishOnly && (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: voicify_spark_darker,
                        color: 'white',
                        fontSize: '9px',
                        fontWeight: 700,
                        padding: '2px 5px',
                        borderRadius: '3px',
                        letterSpacing: '0.5px',
                        flexShrink: 0
                    }}>
                        ES
                    </span>
                )}
            </div>
            {histogramData && (
                <div style={{ fontSize: ttBody }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 0',
                        color: color_text_dashboard_secondary
                    }}>
                        <span>All Conversations</span>
                        <span style={{ fontWeight: 600, color: color_shades_darkest }}>
                            {histogramData.allCalls ?? 0}
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 0',
                        color: color_text_dashboard_secondary
                    }}>
                        <span>Containable Conversations</span>
                        <span style={{ fontWeight: 600, color: color_shades_darkest }}>
                            {histogramData.containableCalls ?? 0}
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 0',
                        color: color_text_dashboard_secondary
                    }}>
                        <span>Contained Conversations</span>
                        <span style={{ fontWeight: 600, color: color_shades_darkest }}>
                            {histogramData.containedCalls ?? 0}
                        </span>
                    </div>
                </div>
            )}
            {!histogramData && value !== undefined && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: subtitle || (showApps && apps.length > 0) ? 6 : 0
                }}>
                    {color && (
                        <div
                            style={{
                                ...tooltipStyles.colorIndicator,
                                backgroundColor: color,
                                border: borderColor ? `2px solid ${borderColor}` : tooltipStyles.colorIndicator.border
                            }}
                        />
                    )}
                    <span style={{ ...tooltipStyles.value, fontSize: ttValue }}>
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </span>
                    <span style={{ ...tooltipStyles.subtitle, fontSize: ttSubtitle }}>
                        {valueLabel}
                    </span>
                </div>
            )}
            {subtitle && (
                <div style={{
                    ...tooltipStyles.subtitle,
                    fontSize: ttSubtitle,
                    paddingTop: 4,
                    borderTop: showApps && apps.length > 0 ? 'none' : `1px solid ${color_shades_light}`,
                    marginBottom: showApps && apps.length > 0 ? 4 : 0
                }}>
                    {subtitle}
                </div>
            )}
            {showApps && apps.length > 0 && (
                <div style={{
                    borderTop: `1px solid ${color_shades_light}`,
                    paddingTop: 8,
                    marginTop: 8
                }}>
                    <div style={{
                        fontSize: ttSmall,
                        color: color_text_dashboard_secondary,
                        marginBottom: 6,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        By Location:
                    </div>
                    {apps
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 5)
                        .map((app, index) => (
                            <div
                                key={index}
                                style={{
                                    padding: '4px 0',
                                    fontSize: ttBody,
                                    color: color_text_dashboard_secondary
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: showLanguageBreakdown && (app.english || app.spanish) ? 2 : 0
                                }}>
                                    <span style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.5,
                                        paddingBottom: 1,
                                        marginRight: 8
                                    }}>
                                        {app.appName}
                                    </span>
                                    <span style={{
                                        fontWeight: 600,
                                        color: color_shades_darkest
                                    }}>
                                        {countPrefix}{typeof app.count === 'number' && countPrefix === '$' ? app.count.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (valueLabel.includes('hours') ? Math.round(app.count) : app.count)}
                                    </span>
                                </div>
                                {showLanguageBreakdown && !spanishOnly && (app.english || app.spanish) && (
                                    <div style={{
                                        display: 'flex',
                                        gap: 8,
                                        marginLeft: 12,
                                        fontSize: 11,
                                        color: color_text_dashboard_secondary
                                    }}>
                                        {app.english !== undefined && app.english > 0 && (
                                            <span>
                                                English: {app.english}
                                            </span>
                                        )}
                                        {app.spanish !== undefined && app.spanish > 0 && (
                                            <span>
                                                Spanish: {app.spanish}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                </div>
            )}
            {clickHint && (
                <div style={{
                    borderTop: `1px solid ${color_shades_light}`,
                    paddingTop: 8,
                    marginTop: 8,
                    fontSize: ttSmall,
                    color: color_text_link,
                    fontStyle: 'italic',
                    textAlign: 'center'
                }}>
                    Click to see more data
                </div>
            )}
        </div>
    );

    return ReactDOM.createPortal(tooltipContent, document.body);
};
